import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { ClipboardList, FileText, Users, Building2, AlertTriangle, Calendar, Clock, ChevronRight, Receipt } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { cn } from '../lib/utils';

const PHASE_LABELS = ['', 'Erfassung', 'Terminierung', 'Rapportieren', 'Verrechnen', 'Abgeschlossen'];
const PHASE_COLORS = ['', 'phase-1', 'phase-2', 'phase-3', 'phase-4', 'phase-5'];

const StatCard = ({ title, value, icon: Icon, color, href }) => (
  <Link to={href || '#'} className="stagger-item block">
    <div className="bg-card border border-border/60 rounded-sm p-6 hover:border-primary/30 hover:shadow-sm transition-all cursor-pointer group">
      <div className="flex items-start justify-between mb-4">
        <div className={cn("w-10 h-10 rounded-sm flex items-center justify-center", color)}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
      </div>
      <p className="font-heading font-black text-3xl text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground mt-1">{title}</p>
    </div>
  </Link>
);

const PhaseBar = ({ label, count, total, colorClass }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-xs font-mono font-medium text-foreground">{count}</span>
    </div>
    <div className="h-1.5 bg-muted rounded-full overflow-hidden">
      <div
        className="h-full bg-primary rounded-full transition-all duration-500"
        style={{ width: total > 0 ? `${(count / total) * 100}%` : '0%' }}
      />
    </div>
  </div>
);

const PendingItem = ({ item, type = 'overdue' }) => {
  const today = new Date().toISOString().split('T')[0];
  const isOverdue = item.deadline < today;
  return (
    <Link
      to={`/orders/${item.id}`}
      data-testid={`pending-item-${item.id}`}
      className={cn(
        "block px-4 py-3 border-l-2 bg-card hover:bg-accent transition-colors rounded-r-sm",
        isOverdue ? "border-destructive overdue-card" : "border-primary/50"
      )}
    >
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{item.title}</p>
          <p className="text-xs text-muted-foreground">{item.order_number} · {item.customer_name || '–'}</p>
        </div>
        <div className="flex items-center gap-2 ml-3 shrink-0">
          <span className={cn("text-xs px-2 py-0.5 rounded-sm font-medium", PHASE_COLORS[item.phase])}>
            {PHASE_LABELS[item.phase]}
          </span>
          {item.deadline && (
            <span className="text-xs text-muted-foreground font-mono">{item.deadline}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard')
      .then(res => setData(res.data))
      .catch(() => toast.error('Dashboard konnte nicht geladen werden'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    </Layout>
  );

  const stats = data?.stats || {};
  const phaseCounts = data?.phase_counts || {};
  const totalOrders = stats.total_orders || 0;

  return (
    <Layout>
      <div className="p-6 md:p-8 space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <h1 className="font-heading font-black text-3xl text-foreground">Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {new Date().toLocaleDateString('de-CH', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard title="Aktive Aufträge" value={stats.total_orders || 0} icon={ClipboardList} color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" href="/orders" />
          <StatCard title="Offerten" value={stats.total_quotes || 0} icon={FileText} color="bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400" href="/quotes" />
          <StatCard title="Verwaltungen" value={stats.total_customers || 0} icon={Users} color="bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400" href="/customers" />
          <StatCard title="Liegenschaften" value={stats.total_properties || 0} icon={Building2} color="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400" href="/properties" />
        </div>

        {/* Offene Rechnungen */}
        {(data?.open_invoices?.length > 0) && (
          <div className="bg-card border border-amber-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Receipt className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                <h3 className="font-heading font-bold text-base text-foreground">Offene Rechnungen</h3>
                <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-sm">{data.open_invoices.length}</span>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground">Gesamtbetrag</p>
                <p className="font-mono font-bold text-sm">CHF {(data.open_invoices_total || 0).toFixed(2)}</p>
              </div>
            </div>
            <div className="space-y-1.5">
              {data.open_invoices.slice(0, 5).map(o => {
                const today = new Date().toISOString().split('T')[0];
                const isOverdue = o.invoice_due_date && o.invoice_due_date < today && o.invoice_sent_at;
                return (
                  <Link key={o.id} to={`/orders/${o.id}`} className={cn(
                    "flex items-center justify-between px-3 py-2 rounded-sm border transition-colors",
                    isOverdue ? "border-red-200 bg-red-50 hover:bg-red-100" : "border-border/60 bg-background hover:bg-accent"
                  )}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{o.title}</p>
                      <p className="text-xs text-muted-foreground">{o.order_number} · {o.customer_name || '–'}</p>
                    </div>
                    <div className="ml-3 text-right shrink-0">
                      {o.totals?.total > 0 && <p className={cn("text-xs font-mono font-medium", isOverdue ? "text-red-600" : "")}>CHF {o.totals.total.toFixed(2)}</p>}
                      {isOverdue && <p className="text-xs text-red-600 font-medium">Überfällig</p>}
                      {o.invoice_due_date && !isOverdue && <p className="text-xs text-muted-foreground">Fällig {new Date(o.invoice_due_date + 'T00:00:00').toLocaleDateString('de-CH')}</p>}
                    </div>
                  </Link>
                );
              })}
              {data.open_invoices.length > 5 && (
                <Link to="/invoices" className="block text-xs text-primary hover:underline text-center pt-1">
                  Alle {data.open_invoices.length} Rechnungen anzeigen →
                </Link>
              )}
            </div>
          </div>
        )}

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Phase Overview */}
          <div className="lg:col-span-4 bg-card border border-border/60 rounded-sm p-6 flex flex-col">
            <h3 className="font-heading font-bold text-base text-foreground mb-5">Aufträge nach Phase</h3>
            <div className="flex-1 min-h-[200px]">
              {totalOrders > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[1,2,3,4,5].map(p => ({ name: PHASE_LABELS[p], value: phaseCounts[`phase_${p}`] || 0 }))}
                      cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value"
                    >
                      { [1,2,3,4,5].map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#64748b'][index]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-muted-foreground">Keine aktiven Aufträge</div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-border/60">
              <Link to="/orders" className="text-sm text-primary hover:underline font-medium" data-testid="all-orders-link">
                Alle Aufträge anzeigen →
              </Link>
            </div>
          </div>

          {/* Overdue */}
          <div className="lg:col-span-4 bg-card border border-border/60 rounded-sm p-6">
            <div className="flex items-center gap-2 mb-5">
              <AlertTriangle className="w-4 h-4 text-destructive" strokeWidth={1.5} />
              <h3 className="font-heading font-bold text-base text-foreground">Überfällig</h3>
              {data?.overdue?.length > 0 && (
                <span className="ml-auto bg-destructive/10 text-destructive text-xs font-medium px-2 py-0.5 rounded-sm">
                  {data.overdue.length}
                </span>
              )}
            </div>
            <div className="space-y-2" data-testid="overdue-list">
              {data?.overdue?.length > 0 ? (
                data.overdue.slice(0, 5).map(item => <PendingItem key={item.id} item={item} />)
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">Keine überfälligen Aufträge</p>
              )}
            </div>
          </div>

          {/* Today & Tomorrow */}
          <div className="lg:col-span-4 space-y-4">
            <div className="bg-card border border-border/60 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-4 h-4 text-primary" strokeWidth={1.5} />
                <h3 className="font-heading font-bold text-base text-foreground">Heute fällig</h3>
                {data?.due_today?.length > 0 && (
                  <span className="ml-auto bg-primary/10 text-primary text-xs font-medium px-2 py-0.5 rounded-sm">
                    {data.due_today.length}
                  </span>
                )}
              </div>
              <div className="space-y-2" data-testid="due-today-list">
                {data?.due_today?.length > 0 ? (
                  data.due_today.map(item => <PendingItem key={item.id} item={item} />)
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-3">Keine Fälligkeiten heute</p>
                )}
              </div>
            </div>
            <div className="bg-card border border-border/60 rounded-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <h3 className="font-heading font-bold text-base text-foreground">Morgen fällig</h3>
                {data?.due_tomorrow?.length > 0 && (
                  <span className="ml-auto bg-muted text-muted-foreground text-xs font-medium px-2 py-0.5 rounded-sm">
                    {data.due_tomorrow.length}
                  </span>
                )}
              </div>
              <div className="space-y-2" data-testid="due-tomorrow-list">
                {data?.due_tomorrow?.length > 0 ? (
                  data.due_tomorrow.map(item => <PendingItem key={item.id} item={item} />)
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-3">Keine Fälligkeiten morgen</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
